import { useState, useRef, useEffect } from 'react'
import type { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { usePerformance } from '@/contexts/PerformanceContext'

interface GlassHoverEffectProps {
  children: (props: {
    containerRef: React.RefObject<HTMLElement | null>
    itemRefs: React.MutableRefObject<(HTMLElement | null)[]>
    containerProps: {
      onMouseEnter: () => void
      onMouseLeave: () => void
      onMouseMove: (e: React.MouseEvent<HTMLElement>) => void
    }
    getItemProps: (index: number) => {
      ref: (el: HTMLElement | null) => void
    }
  }) => ReactNode
  activeIndex?: number
  className?: string
  glassClassName?: string
  direction?: 'horizontal' | 'vertical'
  constrainToContainer?: boolean
  compactMode?: boolean
  compactSize?: number
  /**
   * Delay used when recalculating positions after the component mounts
   * or when nothing special is happening. Keeps things snappy.
   */
  initialUpdateDelayMs?: number
  /**
   * Extra delay to allow parent expand/collapse animations to finish
   * before we measure item sizes (e.g., when sidebar opens).
   */
  expandTransitionMs?: number
  /**
   * If true, update immediately when compactMode toggles, then settle after delay.
   * This removes the perceived lag while still correcting to the final size.
   */
  instantOnModeChange?: boolean
  /**
   * Animation configuration for spring physics
   */
  animationConfig?: {
    stiffness?: number
    damping?: number
    mass?: number
  }
  /**
   * Override performance mode (otherwise uses context)
   */
  forcePerformanceMode?: 'performance' | 'beauty'
}

export const GlassHoverEffect = ({
  children,
  activeIndex = 0,
  className = '',
  glassClassName = '',
  direction = 'horizontal',
  constrainToContainer = false,
  compactMode = false,
  compactSize = 44,
  initialUpdateDelayMs = 50,
  // Sidebar expand animation is ~500ms. Add small buffer.
  expandTransitionMs = 350,
  instantOnModeChange = true,
  animationConfig = {
    stiffness: 700,
    damping: 32,
    mass: 0.75,
  },
  forcePerformanceMode
}: GlassHoverEffectProps) => {
  const { performanceMode: contextMode } = usePerformance()
  const performanceMode = forcePerformanceMode || contextMode
  const isPerformanceMode = performanceMode === 'performance'

  const [blobPosition, setBlobPosition] = useState({ x: 0, y: 0, width: 0, height: 0 })
  const [isHovering, setIsHovering] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)

  const containerRef = useRef<HTMLElement>(null)
  const itemRefs = useRef<(HTMLElement | null)[]>([])
  const previousCompactModeRef = useRef<boolean>(compactMode)

  const updateBlobToActive = () => {
    if (activeIndex !== -1 && itemRefs.current[activeIndex]) {
      const activeItem = itemRefs.current[activeIndex]!
      
      let width = activeItem.offsetWidth
      let height = activeItem.offsetHeight
      let x = activeItem.offsetLeft
      let y = activeItem.offsetTop
      
      // Use compact size when in compact mode (e.g., sidebar collapsed)
      if (compactMode) {
        if (direction === 'horizontal') {
          width = compactSize
          // Center the blob horizontally within the item
          x = activeItem.offsetLeft + (activeItem.offsetWidth - compactSize) / 2
        } else {
          height = compactSize
          // Center the blob vertically within the item
          y = activeItem.offsetTop + (activeItem.offsetHeight - compactSize) / 2
          width = compactSize // Also set width to compact size for square blob
        }
      } else {
        // When not in compact mode, use full item dimensions
        width = activeItem.offsetWidth
        height = activeItem.offsetHeight
        x = activeItem.offsetLeft
        y = activeItem.offsetTop
      }
      
      // Constrain to container only in compact mode (for collapsed sidebar)
      if (constrainToContainer && compactMode && containerRef.current) {
        // Simply constrain to container dimensions to prevent overflow
        const containerWidth = containerRef.current.offsetWidth
        const containerHeight = containerRef.current.offsetHeight
        
        width = Math.min(width, containerWidth - x)
        height = Math.min(height, containerHeight - y)
      }
      
      setBlobPosition({
        x,
        y,
        width,
        height,
      })
    }
  }
  
  useEffect(() => {
    // If we just transitioned between modes, update immediately to remove lag
    const wasCompact = previousCompactModeRef.current
    const modeChanged = wasCompact !== compactMode
    const becomingExpanded = wasCompact && !compactMode
    const delay = becomingExpanded ? expandTransitionMs : initialUpdateDelayMs

    let settleTimer: ReturnType<typeof setTimeout> | undefined

    if (modeChanged && instantOnModeChange) {
      // Immediate rough placement
      updateBlobToActive()
      // Then settle after the relevant delay
      settleTimer = setTimeout(updateBlobToActive, delay)
    } else {
      settleTimer = setTimeout(updateBlobToActive, delay)
    }

    return () => {
      if (settleTimer) clearTimeout(settleTimer)
    }
  }, [activeIndex, compactMode, expandTransitionMs, initialUpdateDelayMs, instantOnModeChange])

  // Track previous compactMode value
  useEffect(() => {
    previousCompactModeRef.current = compactMode
  }, [compactMode])
  
  useEffect(() => {
    if (!isHovering) {
      setIsTransitioning(true)

      // Respect expand animation when becoming expanded to avoid half-size reads
      const wasCompact = previousCompactModeRef.current
      const modeChanged = wasCompact !== compactMode
      const becomingExpanded = wasCompact && !compactMode
      const delay = becomingExpanded ? expandTransitionMs : 0

      const run = () => {
        updateBlobToActive()
        const t = setTimeout(() => setIsTransitioning(false), 220)
        return () => clearTimeout(t)
      }

      if (modeChanged && instantOnModeChange) {
        // Immediate placement first
        updateBlobToActive()
      }

      if (delay > 0) {
        const timer = setTimeout(() => {
          const cleanup = run()
          return cleanup
        }, delay)
        return () => clearTimeout(timer)
      }

      return run()
    }
    return undefined
  }, [activeIndex, isHovering, compactMode, expandTransitionMs, instantOnModeChange])

  const handleMouseEnter = () => {
    setIsHovering(true)
  }

  const handleMouseLeave = () => {
    setIsHovering(false)
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    if (!isHovering || !containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top

    for (const item of itemRefs.current) {
      if (!item) continue
      
      const isWithinBounds = direction === 'horizontal'
        ? mouseX >= item.offsetLeft && mouseX <= item.offsetLeft + item.offsetWidth
        : mouseY >= item.offsetTop && mouseY <= item.offsetTop + item.offsetHeight

      if (isWithinBounds) {
        let width = item.offsetWidth
        let height = item.offsetHeight
        let x = item.offsetLeft
        let y = item.offsetTop
        
        // Use compact size when in compact mode (e.g., sidebar collapsed)
        if (compactMode) {
          if (direction === 'horizontal') {
            width = compactSize
            // Center the blob horizontally within the item
            x = item.offsetLeft + (item.offsetWidth - compactSize) / 2
          } else {
            height = compactSize
            // Center the blob vertically within the item
            y = item.offsetTop + (item.offsetHeight - compactSize) / 2
            width = compactSize // Also set width to compact size for square blob
          }
        } else {
          // When not in compact mode, use full item dimensions
          width = item.offsetWidth
          height = item.offsetHeight
          x = item.offsetLeft
          y = item.offsetTop
        }
        
        // Constrain to container only in compact mode (for collapsed sidebar)
        if (constrainToContainer && compactMode && containerRef.current) {
          // Simply constrain to container dimensions to prevent overflow
          const containerWidth = containerRef.current.offsetWidth
          const containerHeight = containerRef.current.offsetHeight
          
          width = Math.min(width, containerWidth - x)
          height = Math.min(height, containerHeight - y)
        }
        
        const newPosition = {
          x,
          y,
          width,
          height,
        }
        
        const hasChanged = blobPosition.x !== newPosition.x || 
                          blobPosition.y !== newPosition.y ||
                          blobPosition.width !== newPosition.width || 
                          blobPosition.height !== newPosition.height

        if (hasChanged) {
          setBlobPosition(newPosition)
        }
        return
      }
    }
    
    return
  }

  const containerProps = {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onMouseMove: handleMouseMove,
  }

  const getItemProps = (index: number) => ({
    ref: (el: HTMLElement | null) => { 
      itemRefs.current[index] = el 
    }
  })

  return (
    <div 
      className={`relative ${className}`}
      data-hovering={isHovering}
      data-compact={compactMode}
      data-direction={direction}
      data-performance-mode={performanceMode}
    >
      {/* SVG Filters for Glass Effect */}
      <svg style={{ display: 'none' }}>
        {/* Performance Mode - Lightweight filter */}
        {isPerformanceMode ? (
          <filter
            id="glass-distortion-light"
            x="0%"
            y="0%"
            width="100%"
            height="100%"
            filterUnits="objectBoundingBox"
            primitiveUnits="objectBoundingBox"
          >
            <feGaussianBlur
              in="SourceGraphic"
              stdDeviation="0.005"
              result="blur"
            />
            <feOffset in="blur" dx="0.002" dy="0.002" result="offsetBlur" />
            <feBlend in="SourceGraphic" in2="offsetBlur" mode="normal" />
          </filter>
        ) : (
          /* Beauty Mode - High-quality filter */
          <filter
            id="glass-distortion"
            x="0%"
            y="0%"
            width="100%"
            height="100%"
            filterUnits="objectBoundingBox"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.001 0.005"
              numOctaves="1"
              seed="17"
              result="turbulence"
            />
            <feComponentTransfer in="turbulence" result="mapped">
              <feFuncR type="gamma" amplitude="1" exponent="10" offset="0.5" />
              <feFuncG type="gamma" amplitude="0" exponent="1" offset="0" />
              <feFuncB type="gamma" amplitude="0" exponent="1" offset="0.5" />
            </feComponentTransfer>
            <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />
            <feSpecularLighting
              in="softMap"
              surfaceScale="5"
              specularConstant="1"
              specularExponent="100"
              lightingColor="white"
              result="specLight"
            >
              <fePointLight x="-200" y="-200" z="300" />
            </feSpecularLighting>
            <feComposite
              in="specLight"
              operator="arithmetic"
              k1="0"
              k2="1"
              k3="1"
              k4="0"
              result="litImage"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="softMap"
              scale="50"
              xChannelSelector="R"
              yChannelSelector="G"
            />
          </filter>
        )}
      </svg>

      {/* Glass Blob */}
      <motion.div
        className={`absolute rounded-lg z-0 pointer-events-none shadow-sm ${glassClassName}`}
        animate={{
          x: blobPosition.x,
          y: blobPosition.y,
          width: blobPosition.width,
          height: blobPosition.height,
        }}
        transition={
          isPerformanceMode
            ? {
                type: 'tween',
                duration: 0.15,
                ease: 'easeOut',
              }
            : {
                type: 'spring',
                stiffness: animationConfig?.stiffness ?? 350,
                damping: animationConfig?.damping ?? 30,
                mass: animationConfig?.mass ?? 0.9,
              }
        }
        style={{
          transition: isHovering
            ? 'transform 0.12s ease-out, filter 0.12s ease-out'
            : 'transform 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94), filter 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          transform: isTransitioning 
            ? 'scale(1.025)' 
            : isHovering 
              ? 'scale(1.01)' 
              : 'scale(1)',
          filter: `blur(${
            isPerformanceMode 
              ? '0px' 
              : isTransitioning 
                ? '0.6px' 
                : '0.2px'
          })`,
          willChange: isHovering ? 'transform' : 'auto',
        }}
        data-transitioning={isTransitioning}
      >
        {/* Primary Glass Layer - Light Mode */}
        <div
          className="absolute inset-0 rounded-lg backdrop-blur-sm dark:hidden"
          style={{
            background: isPerformanceMode
              ? 'rgba(255,255,255,0.4)'
              : `linear-gradient(135deg, 
                  rgba(255,255,255,0.5) 0%, 
                  rgba(255,255,255,0.3) 30%, 
                  rgba(255,255,255,0.25) 70%,
                  rgba(255,255,255,0.35) 100%)`,
            boxShadow: isPerformanceMode
              ? '0 0 0 1px rgba(59,130,246,0.15), 0 2px 8px rgba(59,130,246,0.08)'
              : `0 0 0 1px rgba(59,130,246,0.15),
                  inset 0 2px 4px rgba(255,255,255,0.6), 
                  inset 0 -2px 4px rgba(255,255,255,0.4),
                  inset 2px 0 4px rgba(255,255,255,0.3),
                  inset -2px 0 4px rgba(255,255,255,0.3),
                  0 4px 12px rgba(59,130,246,0.08)`,
          }}
        ></div>

        {/* Primary Glass Layer - Dark Mode */}
        <div
          className="absolute inset-0 rounded-lg backdrop-blur-sm hidden dark:block"
          style={{
            background: isPerformanceMode
              ? 'rgba(255,255,255,0.06)'
              : `linear-gradient(135deg, 
                  rgba(255,255,255,0.08) 0%, 
                  rgba(255,255,255,0.05) 30%, 
                  rgba(255,255,255,0.04) 70%,
                  rgba(255,255,255,0.06) 100%)`,
            boxShadow: isPerformanceMode
              ? '0 0 0 1px rgba(96,165,250,0.2), 0 2px 8px rgba(96,165,250,0.12)'
              : `0 0 0 1px rgba(96,165,250,0.2),
                  inset 0 2px 4px rgba(255,255,255,0.1), 
                  inset 0 -2px 4px rgba(255,255,255,0.05),
                  inset 2px 0 4px rgba(255,255,255,0.05),
                  inset -2px 0 4px rgba(255,255,255,0.05),
                  0 4px 12px rgba(96,165,250,0.12)`,
          }}
        ></div>

        {/* Glass Refraction Layer - Only in Beauty Mode */}
        {!isPerformanceMode && (
          <>
            {/* Light Mode */}
            <div
              className="absolute inset-0 rounded-lg dark:hidden"
              style={{
                background: `linear-gradient(135deg, 
                    rgba(59,130,246,0.04) 0%, 
                    transparent 25%,
                    rgba(147,51,234,0.025) 75%,
                    transparent 100%)`,
                filter: 'url(#glass-distortion)',
              }}
            ></div>

            {/* Dark Mode */}
            <div
              className="absolute inset-0 rounded-lg hidden dark:block"
              style={{
                background: `linear-gradient(135deg, 
                    rgba(96,165,250,0.06) 0%, 
                    transparent 25%,
                    rgba(167,139,250,0.04) 75%,
                    transparent 100%)`,
                filter: 'url(#glass-distortion)',
              }}
            ></div>
          </>
        )}

        {/* Dynamic Liquid Highlight - Light Mode */}
        <div
          className="absolute inset-0 rounded-lg transition-all duration-300 dark:hidden"
          style={{
            background: `radial-gradient(circle at center, 
                rgba(59,130,246,0.1) 0%, 
                rgba(59,130,246,0.03) 50%, 
                transparent 70%)`,
            opacity: isPerformanceMode ? 0.15 : 0.25,
          }}
        ></div>

        {/* Dynamic Liquid Highlight - Dark Mode */}
        <div
          className="absolute inset-0 rounded-lg transition-all duration-300 hidden dark:block"
          style={{
            background: `radial-gradient(circle at center, 
                rgba(96,165,250,0.12) 0%, 
                rgba(96,165,250,0.04) 50%, 
                transparent 70%)`,
            opacity: isPerformanceMode ? 0.2 : 0.3,
          }}
        ></div>
      </motion.div>

      {/* Content */}
      {children({ containerRef, itemRefs, containerProps, getItemProps })}
    </div>
  )
}
